import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const ApprovalSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { action, requestId, userDisplayName } = location.state || {};

  const isApproved = action === 'approved';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {/* Success Icon */}
          <div className={`mx-auto flex items-center justify-center h-20 w-20 rounded-full ${
            isApproved ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {isApproved ? (
              <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="h-10 w-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>

          {/* Success Message */}
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {isApproved ? 'Request Approved!' : 'Request Rejected'}
          </h2>
          
          <p className="mt-2 text-sm text-gray-600">
            {isApproved 
              ? `The role change request for ${userDisplayName} has been approved and is being processed.`
              : `The role change request for ${userDisplayName} has been rejected.`
            }
          </p>

          {/* Request Details */}
          <div className="mt-6 bg-white shadow rounded-lg p-6">
            <dl className="space-y-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Request ID</dt>
                <dd className="text-sm text-gray-900 font-mono">{requestId}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">User</dt>
                <dd className="text-sm text-gray-900">{userDisplayName}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="text-sm">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    isApproved 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {isApproved ? 'Approved' : 'Rejected'}
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          {/* Next Steps */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              {isApproved ? 'What happens next?' : 'What happens next?'}
            </h3>
            <div className="text-left space-y-2 text-sm text-gray-600">
              {isApproved ? (
                <>
                  <p>• The SAP role change is being processed automatically</p>
                  <p>• You will receive a confirmation email once completed</p>
                  <p>• The user will be notified of the approval</p>
                  <p>• The request will be marked as completed in the system</p>
                </>
              ) : (
                <>
                  <p>• The user will be notified of the rejection</p>
                  <p>• The request has been marked as rejected in the system</p>
                  <p>• No SAP changes have been made</p>
                  <p>• The user can submit a new request if needed</p>
                </>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 space-y-3">
            <button
              onClick={() => window.close()}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Close This Tab
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Return to Dashboard
            </button>
            
            <button
              onClick={() => navigate('/requests')}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              View All Requests
            </button>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500">
              This action has been logged for audit purposes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalSuccessPage;
